import type { GraphQLResponse } from "graphql-request";

import type { MagentoApiAddressValidationErrorCode } from "@jti/magento-api-v2";

const ADDRESS_VALIDATION_CATEGORY = "address-validation" as const;

type GraphQLError = NonNullable<GraphQLResponse["errors"]>[number];

type GraphQLErrorWithAnyExtensions = Omit<GraphQLError, "extensions"> & Pick<GraphQLError, "extensions">;

type ExternalAddressValidationErrorExtentention = {
  category: typeof ADDRESS_VALIDATION_CATEGORY;
  code: MagentoApiAddressValidationErrorCode;
};

type GraphQLErrorWithAddressValidationExtension = GraphQLErrorWithAnyExtensions & {
  extensions: ExternalAddressValidationErrorExtentention;
};

const isErrorWithExtensions = (error: unknown): error is GraphQLErrorWithAnyExtensions => {
  return (
    typeof error === "object" &&
    error !== null &&
    "extensions" in error &&
    typeof error.extensions === "object" &&
    error.extensions !== null
  );
};

const isMagentoApiAddressValidationErrorCode = (code: unknown): code is MagentoApiAddressValidationErrorCode => {
  if (typeof code !== "string") {
    return false;
  }

  return (
    code === "INVALID_ADDRESS" ||
    code === "INVALID_CITY" ||
    code === "INVALID_POSTCODE" ||
    code === "INVALID_PROVINCE" ||
    code === "INVALID_TELEPHONE" ||
    code === "INVALID_SA"
  );
};

const toGraphqlErrorsWithExtensions = (errors: unknown): GraphQLErrorWithAnyExtensions[] => {
  if (!Array.isArray(errors)) {
    return [];
  }

  return errors.filter((item): item is GraphQLErrorWithAnyExtensions => isErrorWithExtensions(item));
};

const isExternalAddressValidationGraphqlError = (graphqlError: GraphQLErrorWithAnyExtensions): boolean => {
  const extensions = graphqlError.extensions;

  return (
    extensions?.category === ADDRESS_VALIDATION_CATEGORY ||
    isMagentoApiAddressValidationErrorCode((extensions as any)?.code)
  );
};

const hasExternalAddressValidationCode = (
  graphqlError: GraphQLErrorWithAnyExtensions
): graphqlError is GraphQLErrorWithAddressValidationExtension => {
  const extensions = graphqlError.extensions;

  return (
    extensions?.category === ADDRESS_VALIDATION_CATEGORY &&
    isMagentoApiAddressValidationErrorCode((extensions as any)?.code)
  );
};

const getGraphqlErrorsFromUnknownError = (error: unknown): GraphQLErrorWithAnyExtensions[] => {
  if (!error) {
    return [];
  }

  if (Array.isArray(error)) {
    return toGraphqlErrorsWithExtensions(error);
  }

  if (
    typeof error === "object" &&
    "response" in error &&
    typeof error.response === "object" &&
    error.response !== null &&
    "errors" in error.response
  ) {
    const responseErrors = error.response.errors;

    return toGraphqlErrorsWithExtensions(responseErrors);
  }

  return [];
};

export const hasExternalAddressValidationError = (error: unknown): error is GraphQLErrorWithAnyExtensions => {
  const errorsWithExternalValidation = getGraphqlErrorsFromUnknownError(error);

  return errorsWithExternalValidation.some(isExternalAddressValidationGraphqlError);
};

export const getExternalAddressValidationErrorCode = (
  error: unknown
): MagentoApiAddressValidationErrorCode | undefined => {
  const graphqlErrors = getGraphqlErrorsFromUnknownError(error);
  const errorWithAddressValidationDetails = graphqlErrors.find((graphqlError) =>
    hasExternalAddressValidationCode(graphqlError)
  );

  if (!errorWithAddressValidationDetails) {
    return undefined;
  }

  return errorWithAddressValidationDetails.extensions.code;
};
